import { FC } from "react";
import styled from "styled-components/macro";
import { motion } from "framer-motion";
import GitHubIcon from "@mui/icons-material/GitHub";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import EmailIcon from "@mui/icons-material/Email";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

const FooterContainer = styled.footer`
  width: 100%;
  background: ${({ theme }) => theme.footerBackground};
  backdrop-filter: blur(10px);
  font-family: "DM Sans", serif;
  border-top: none;
  margin-top: auto;
  position: relative;
  overflow: hidden;
  text-align: left;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
  }
`;

const FooterContent = styled(motion.div)`
  max-width: 1200px;
  margin: 0 auto;
  padding: 60px 40px 40px;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 40px;
  align-items: start;
  @media screen and (max-width: 1000px) {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 30px;
  }
`;

const BrandSection = styled(motion.div)`
  .brand-name {
    font-size: 26px;
    font-weight: 400;
    color: white;
    font-family: "DM Sans", serif;
    margin-bottom: 16px;
    @media screen and (max-width: 1000px) {
      display: none;
    }
  }

  .brand-tagline {
    color: rgba(255, 255, 255, 0.8);
    font-size: 16px;
    line-height: 1.6;
    margin-bottom: 24px;
    font-family: "DM Sans", serif;
    @media screen and (max-width: 1000px) {
      display: none;
    }
  }

  .social-links {
    display: flex;
    gap: 16px;
  }
`;

const FooterSection = styled(motion.div)`
  h3 {
    color: white;
    font-size: 18px;
    font-weight: 600;
    font-family: "DM Sans", serif;
    letter-spacing: 0.5px;
    margin: 8px 0px 20px 0px;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    margin-bottom: 12px;
  }
  @media screen and (max-width: 1000px) {
    display: none;
  }
`;

const SocialButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: white;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  }

  svg {
    font-size: 20px;
  }
`;

const FooterBottom = styled.div`
  padding: 24px 40px;
  background: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(5px);

  @media screen and (max-width: 1000px) {
    padding: 20px;
    text-align: center;
  }
`;

const FooterBottomContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;

  @media screen and (max-width: 1000px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const Copyright = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  margin: 0;
  font-family: "DM Sans", serif;
`;

const BackToTop = styled(motion.button)`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 12px 16px;
  border-radius: 25px;
  cursor: pointer;
  font-size: 14px;
  font-family: "DM Sans", serif;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }

  svg {
    font-size: 16px;
  }
`;

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};
const YEAR = new Date().getFullYear();
const Footer: FC = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <FooterContainer>
      <FooterContent
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
      >
        <BrandSection variants={itemVariants}>
          <div className="brand-name">Alexandra Veres</div>
          <div className="brand-tagline">
            Thank you for taking the time to explore my work. I’m a developer
            who cares about detail, learning, and bringing ideas to life. If
            anything sparks your interest or you’d like to collaborate, feel
            free to reach out or connect!
          </div>
          <div className="social-links">
            <SocialButton
              href="https://github.com/xandrav"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub Profile"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <GitHubIcon />
            </SocialButton>
            <SocialButton
              href="https://www.linkedin.com/in/alexandra-veres95/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn Profile"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <LinkedInIcon />
            </SocialButton>
            <SocialButton
              href="mailto:veresalexandra95@gmail.com"
              aria-label="Email Contact"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <EmailIcon />
            </SocialButton>
          </div>
        </BrandSection>

        <FooterSection variants={itemVariants}>
          <h3>Specialties</h3>
          <ul>
            <li>
              <span style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                Frontend Development
              </span>
            </li>
            <li>
              <span style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                UX/UI Design
              </span>
            </li>

            <li>
              <span style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                Data Visualisation
              </span>
            </li>
            <li>
              <span style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                Machine Learning
              </span>
            </li>
          </ul>
        </FooterSection>
        <FooterSection variants={itemVariants}>
          <h3>Languages</h3>
          <ul>
            <li>
              <span style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                TypeScript
              </span>
            </li>
            <li>
              <span style={{ color: "rgba(255, 255, 255, 0.8)" }}>Python</span>
            </li>
            <li>
              <span style={{ color: "rgba(255, 255, 255, 0.8)" }}>Java</span>
            </li>
          </ul>
        </FooterSection>
        <FooterSection variants={itemVariants}>
          <h3>Values</h3>
          <ul>
            <li>
              <span style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                Collaboration
              </span>
            </li>
            <li>
              <span style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                Learning
              </span>
            </li>
            <li>
              <span style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                Creativity
              </span>
            </li>
            <li>
              <span style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                Teamwork
              </span>
            </li>
          </ul>
        </FooterSection>
      </FooterContent>

      <FooterBottom>
        <FooterBottomContent>
          <Copyright>© {YEAR} Alexandra Veres. All rights reserved.</Copyright>
          <BackToTop
            onClick={scrollToTop}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <KeyboardArrowUpIcon />
            Back to Top
          </BackToTop>
        </FooterBottomContent>
      </FooterBottom>
    </FooterContainer>
  );
};

export default Footer;
